import { createRoute, redirect } from '@tanstack/react-router'
import { useState } from 'react'
import { rootRoute } from './__root'
import { apiClient } from '@/lib/api'
import { AppHeader } from '@/components/AppHeader'
import { UpgradeDialog } from '@/components/panels/UpgradeDialog'
import { useAuth } from '@/hooks/useAuth'
import { useEffectiveFeatures } from '@/hooks/useEffectiveFeatures'
import { useBillingPortal } from '@/hooks/useBilling'
import { usePlatform } from '@/hooks/usePlatform'
import { AppTile } from '@/components/home/AppTile'
import { LatestLayout } from '@/components/home/widgets/LatestLayout'
import { LatestProject } from '@/components/home/widgets/LatestProject'
import { MidiStatus } from '@/components/home/widgets/MidiStatus'
import { PlanUsage } from '@/components/home/widgets/PlanUsage'

export const homeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/home',
  beforeLoad: async () => {
    try {
      await apiClient.get('/api/v1/auth/me')
    } catch {
      // Any failure — unauthenticated, network error, backend down —
      // sends the user to /login.
      throw redirect({ to: '/login' })
    }
  },
  component: HomePage,
})

function HomePage() {
  const { user } = useAuth()
  // Reads through the DEV tier override when set, so the bottom-right
  // tier switcher actually flips the "FREE PLAN" badge up top.
  // Billing decisions still read `user.tier` directly (see
  // UpgradeDialog / UserMenu) — only the header label uses this.
  const { tier: effectiveTier } = useEffectiveFeatures()
  const platform = usePlatform()
  const openPortal = useBillingPortal()
  const [upgradeOpen, setUpgradeOpen] = useState(false)

  const currentTier = effectiveTier ?? user?.tier ?? 'free'
  const hasActiveSubscription = user?.subscription?.hasActiveSubscription
  const tierLabel =
    currentTier === 'free'
      ? 'Free plan'
      : currentTier === 'pro'
        ? 'Pro plan'
        : 'Studio plan'
  const tierBadgeClass =
    currentTier === 'free'
      ? 'bg-surface-sunken text-content-secondary'
      : currentTier === 'pro'
        ? 'bg-accent-muted text-accent'
        : 'bg-[color:var(--second-soft)] text-second'

  const handleManageBilling = async () => {
    try {
      const { url } = await openPortal.mutateAsync()
      window.location.href = url
    } catch (err) {
      console.error('Billing portal failed', err)
    }
  }

  return (
    <div className="min-h-[100dvh] bg-surface-base">
      <AppHeader
        title="Busswerks"
        subtitle="Your audio workspace"
        compact
        hideAuthAction
      >
        <span
          className={`hidden md:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider ${tierBadgeClass}`}
          title={`You're on the ${currentTier} plan`}
        >
          {tierLabel}
        </span>
        {platform.billingDisabled ? (
          <button
            onClick={() => setUpgradeOpen(true)}
            className="btn-secondary btn-sm"
            title="Subscription plans are managed on the web"
          >
            Manage plan
          </button>
        ) : hasActiveSubscription ? (
          <button
            onClick={handleManageBilling}
            disabled={openPortal.isPending}
            className="btn-secondary btn-sm disabled:opacity-50"
            title="Manage subscription, download invoices, update payment method"
          >
            {openPortal.isPending ? 'Opening\u2026' : 'Manage billing'}
          </button>
        ) : (
          <button
            onClick={() => setUpgradeOpen(true)}
            className="btn-accent-gradient btn-sm font-semibold"
          >
            Upgrade
          </button>
        )}
      </AppHeader>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8 flex flex-col gap-6">
        {/* Quick-access widget row sits ABOVE the apps grid so the
            user's latest work is front-and-centre. Three equal
            columns: Latest environment (Bussboard), Latest Project
            (Werkshop), MIDI Status. Each widget self-hides when it
            has no data so the grid collapses cleanly on empty states
            (e.g. brand new user). */}
        <section>
          <h2 className="text-[10px] font-semibold uppercase tracking-[0.14em] text-content-tertiary mb-3">
            Quick access
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <LatestLayout />
            <LatestProject />
            <MidiStatus />
          </div>
        </section>

        <section>
          <h2 className="text-[10px] font-semibold uppercase tracking-[0.14em] text-content-tertiary mb-3">
            Apps
          </h2>
          {/* 4-col at lg so the four apps fit on one row; collapse to
              2 at sm and 1 on mobile. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <AppTile
              icon="B"
              title="Bussboard"
              description="Map any signal-flow rig — studio, live stage, broadcast truck, camera + lighting — on a node-graph canvas."
              href="/bussboard"
              accent="accent"
            />
            <AppTile
              icon="W"
              title="Werkshop"
              description="Sequencer, MIDI router, sampler, and module store — the DAW side of the platform."
              href="/werkshop"
              accent="second"
            />
          </div>
        </section>

        <PlanUsage onUpgrade={() => setUpgradeOpen(true)} />
      </main>

      <UpgradeDialog
        isOpen={upgradeOpen}
        onClose={() => setUpgradeOpen(false)}
      />
    </div>
  )
}
